<template>
  <v-card flat height="calc(100vh - 106px)" class="overflow-y">
    <v-expansion-panels popout class="pa-8">

      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">General</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container fluid>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.equityBalance" label="Opening balance account"
                                :accounts="equityAccounts" :addTypes="['Equity']"
                                hint="To balance initial values of new inventory"/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.exciseLiability" label="Excise liability account"
                                :accounts="liabilityAccounts" :addTypes="['OtherCurrentLiability']"
                                hint="To accrue excise liability"/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.inventoryAdjustment" label="Default inventory adjustment account"
                                :accounts="expenseAccounts" :addTypes="['Expense','OtherExpense','CostOfGoodsSold']"
                                hint="To use when adjusting inventory"/>
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <component :is="province" :accounts="accounts"/>

      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Items</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container fluid>
            <v-row class="mb-4">
              <v-col class="text-caption">These are default accounts. Individual items can override these accounts.</v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.itemInventory" label="Default item inventory account"
                                :accounts="inventoryAccounts" :addTypes="['OtherCurrentAsset']"
                                hint="To track the value of item inventory."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.itemCOG" label="Default item COG account"
                                :accounts="cogAccounts" :addTypes="['CostOfGoodsSold']"
                                hint="To track sales of inventory items."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.itemIncome" label="Default item income account"
                                :accounts="incomeAccounts" :addTypes="['Income']"
                                hint="To track sales of inventory items."/>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.nonInventoryUsage" label="Default non-inventory item usage account"
                                :accounts="expenseAccounts" :addTypes="['Expense','OtherExpense']"
                                hint="To track production usage of non-inventory items."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.nonInventoryExpense" label="Default non-inventory item expense account"
                                :accounts="expenseAccounts" :addTypes="['Expense','OtherExpense','CostOfGoodsSold']"
                                hint="To track purchasing of non-inventory items."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.nonInventoryIncome" label="Default non-inventory item income account"
                                :accounts="incomeAccounts" :addTypes="['Income']"
                                hint="To track sales of non-inventory items."/>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.serviceUsage" label="Default service item usage account"
                                :accounts="expenseAccounts" :addTypes="['Expense','OtherExpense']"
                                hint="To track production usage of service items."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.serviceExpense" label="Default service item expense account"
                                :accounts="expenseAccounts" :addTypes="['Expense','OtherExpense','CostOfGoodsSold']"
                                hint="To track purchasing of service items."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.serviceIncome" label="Default service item income account"
                                :accounts="incomeAccounts" :addTypes="['Income']"
                                hint="To track sales of service items."/>
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Casks</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container fluid>
            <v-row class="mb-4">
              <v-col class="text-caption">These are default accounts. Individual casks can override these accounts.</v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.caskInventory" label="Default cask inventory account"
                                :accounts="inventoryAccounts" :addTypes="['OtherCurrentAsset']"
                                hint="To track the value of cask inventory."/>
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Spirits</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container fluid>
            <v-row class="mb-4">
              <v-col class="text-caption">These are default accounts. Individual spirits can override these accounts.</v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.spiritInventory" label="Default spirit inventory account"
                                :accounts="inventoryAccounts" :addTypes="['OtherCurrentAsset']"
                                hint="To track the value of spirits in bulk containers."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.spiritCOG" label="Default spirit COG account"
                                :accounts="cogAccounts" :addTypes="['CostOfGoodsSold']"
                                hint="To track sales of spirits."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.spiritIncome" label="Default spirit income account"
                                :accounts="incomeAccounts" :addTypes="['Income']"
                                hint="To track sales of spirits."/>
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Non-excise</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container fluid>
            <v-row class="mb-4">
              <v-col class="text-caption">These are default accounts. Individual non-excise alcohols can override these accounts.</v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.nonExciseInventory" label="Default non-excise inventory account"
                                :accounts="inventoryAccounts" :addTypes="['OtherCurrentAsset']"
                                hint="To track the value of non-excise alcohols in bulk containers."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.nonExciseCOG" label="Default non-excise COG account"
                                :accounts="cogAccounts" :addTypes="['CostOfGoodsSold']"
                                hint="To track sales of non-excise alcohols."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.nonExciseIncome" label="Default non-excise income account"
                                :accounts="incomeAccounts" :addTypes="['Income']"
                                hint="To track sales of non-excise alcohols."/>
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Wine</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container fluid>
            <v-row class="mb-4">
              <v-col class="text-caption">These are default accounts. Individual wines can override these accounts.</v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.wineInventory" label="Default wine inventory account"
                                :accounts="inventoryAccounts" :addTypes="['OtherCurrentAsset']"
                                hint="To track the value of wine in bulk containers."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.wineCOG" label="Default wine COG account"
                                :accounts="cogAccounts" :addTypes="['CostOfGoodsSold']"
                                hint="To track sales of wine."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.wineIncome" label="Default wine income account"
                                :accounts="incomeAccounts" :addTypes="['Income']"
                                hint="To track sales of wine."/>
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Products</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container fluid>
            <v-row class="mb-4">
              <v-col class="text-caption">These are default accounts. Individual products can override these accounts.</v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.bondedProductInventory" label="Default in-bond packaged inventory account"
                                :accounts="inventoryAccounts" :addTypes="['OtherCurrentAsset']"
                                hint="To track the value of in-bond products."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.bondedProductCOG" label="Default in-bond packaged COG account"
                                :accounts="cogAccounts" :addTypes="['CostOfGoodsSold']"
                                hint="To track sales of in-bond products."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.bondedProductIncome" label="Default in-bond packaged income account"
                                :accounts="incomeAccounts" :addTypes="['Income']"
                                hint="To track sales of in-bond products."/>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.dutyPaidProductInventory" label="Default duty-paid packaged inventory account"
                                :accounts="inventoryAccounts" :addTypes="['OtherCurrentAsset']"
                                hint="To track the value of duty paid products."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.dutyPaidProductCOG" label="Default duty-paid packaged COG account"
                                :accounts="cogAccounts" :addTypes="['CostOfGoodsSold']"
                                hint="To track sales of duty paid products."/>
              </v-col>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.dutyPaidProductIncome" label="Default duty-paid packaged income account"
                                :accounts="incomeAccounts" :addTypes="['Income']"
                                hint="To track sales of duty paid products."/>
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Bundles</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container fluid>
            <v-row class="mb-4">
              <v-col class="text-caption">This is a default account. Individual bundles can override this account.</v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.bundleIncome" label="Default bundle income account"
                                :accounts="incomeAccounts" :addTypes="['Income']"
                                hint="To track sales of bundles."/>
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header class="text-h6">Tanks and Fermenters</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container fluid>
            <v-row class="mb-4">
              <v-col class="text-caption">These are default accounts. Individual vessels can override these accounts.</v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.tankInventory" label="Default tank inventory account"
                                :accounts="inventoryAccounts" :addTypes="['OtherCurrentAsset']"
                                hint="To track the value of tank contents."/>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <account-picker v-model="companyAccounts.fermenterInventory" label="Default fermenter inventory account"
                                :accounts="inventoryAccounts" :addTypes="['OtherCurrentAsset']"
                                hint="To track the value of fermenter contents."/>
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>

    </v-expansion-panels>
  </v-card>
</template>

<script>
export default {
  name: 'AppsQboAccounts',
  data() {
    return {
      loaded: false,
      saving: false,
      accounts: [],
      companyAccounts: {},
      province: null
    }
  },
  computed: {
    equityAccounts() {
      return this.accounts.filter(account => account.type === 'Equity');
    },
    expenseAccounts() {
      return this.accounts.filter(account => account.type === 'Expense' || account.type === 'OtherExpense' || account.type === 'CostOfGoodsSold');
    },
    cogAccounts() {
      return this.accounts.filter(account => account.type === 'CostOfGoodsSold');
    },
    liabilityAccounts() {
      return this.accounts.filter(account => account.type === 'OtherCurrentLiability');
    },
    inventoryAccounts() {
      return this.accounts.filter(account => account.type === 'OtherCurrentAsset');
    },
    incomeAccounts() {
      return this.accounts.filter(account => account.type === 'Income');
    },
    company() {
      return this.$store.getters.company;
    }
  },
  async created() {
    this.load();
    this.province = () => import(`@/views/provinces/${this.company.province}/configuration/QboAccounts`);
  },
  watch: {
    companyAccounts: {
      handler() {if (this.loaded) this.saveCompanyAccounts()},
      deep: true
    }
  },

  methods: {
    async load() {
      const results = await this.$apiCall(`query AppsQboAccounts($currency:String!) {
                                                accounts(currency:$currency) {
                                                  ref,parentRef,name,type,typeName
                                                }
                                                companyAccounts {
                                                  equityBalance,exciseLiability,inventoryAdjustment,
                                                  itemInventory,itemCOG,itemIncome,
                                                  nonInventoryUsage,nonInventoryExpense,nonInventoryIncome,
                                                  serviceUsage,serviceExpense,serviceIncome,
                                                  caskInventory,
                                                  spiritInventory,spiritCOG,spiritIncome,
                                                  nonExciseInventory,nonExciseCOG,nonExciseIncome,
                                                  wineInventory,wineCOG,wineIncome,
                                                  bondedProductInventory,bondedProductCOG,bondedProductIncome,
                                                  dutyPaidProductInventory,dutyPaidProductCOG,dutyPaidProductIncome,
                                                  bundleIncome,
                                                  fermenterInventory,tankInventory
                                                }
                                              }`,
                                          {currency:this.company.settings.homeCurrency});
      if (!results.error) {
        this.accounts = results.data.accounts;
        this.companyAccounts = results.data.companyAccounts;
        this.$nextTick(() => {
          this.loaded = true;
        });
      }
    },
    async saveCompanyAccounts() {
      this.saving = true;
      const results = await this.$apiCall(`mutation AppsQboAccounts($input:CompanyAccountsInput!) {
                                             companySetAccounts(input:$input) {
                                                equityBalance
                                              }
                                           }`,
                                          {input:this.companyAccounts});
      this.saving = false;
    }
  }
}
</script>